import { useState } from "react";
import { useUserProfile, useUpdateProfile, useUploadAvatar } from "@/hooks/useUserProfile";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Camera, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

function useClientInfo() {
  const { data: profile } = useUserProfile();
  return useQuery({
    queryKey: ["portal-client-info", (profile as any)?.client_id],
    enabled: !!(profile as any)?.client_id,
    queryFn: async () => {
      const { data } = await supabase
        .from("client")
        .select("name, code, contact_email, phone")
        .eq("id", (profile as any)?.client_id)
        .single();
      return data ?? null;
    },
  });
}

function useOrgInfo() {
  const { data: profile } = useUserProfile();
  return useQuery({
    queryKey: ["portal-org-info", (profile as any)?.org_id],
    enabled: !!(profile as any)?.org_id,
    queryFn: async () => {
      const { data } = await supabase
        .from("organization")
        .select("name, logo_url")
        .eq("id", (profile as any)?.org_id)
        .single();
      return data ?? null;
    },
  });
}

export default function CustomerProfile() {
  const { user } = useAuth();
  const { data: profile, isLoading } = useUserProfile();
  const { data: clientInfo } = useClientInfo();
  const { data: orgInfo } = useOrgInfo();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const initials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : "U";

  function startEditing() {
    setDisplayName(profile?.display_name || "");
    setIsEditing(true);
  }

  function handleSave() {
    if (displayName.trim().length < 2) return;
    updateProfile.mutate({ display_name: displayName.trim() });
    setIsEditing(false);
  }

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadAvatar.mutate(file);
  }

  if (isLoading) {
    return (
      <div className="flex-1 p-6 lg:p-8 max-w-2xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 lg:p-8 max-w-2xl mx-auto space-y-6">
      <button
        onClick={() => navigate("/portal")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to overview
      </button>

      <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>

      {/* Avatar + Name */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profile?.avatar_url || ""} />
                <AvatarFallback className="text-lg">{initials}</AvatarFallback>
              </Avatar>
              <label className="absolute -bottom-1 -right-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors">
                <Camera className="h-3.5 w-3.5" />
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleAvatarChange}
                />
              </label>
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="max-w-xs"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  />
                  <Button size="sm" onClick={handleSave} disabled={updateProfile.isPending}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-lg font-semibold">{profile?.display_name || "—"}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <Button size="sm" variant="link" className="h-auto p-0 mt-1" onClick={startEditing}>
                    Edit name
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client Info */}
      {clientInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your Account</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3 text-sm">
              <dt className="text-muted-foreground">Client</dt>
              <dd className="font-medium">{clientInfo.name}</dd>
              {clientInfo.code && (
                <>
                  <dt className="text-muted-foreground">Client Code</dt>
                  <dd className="font-mono">{clientInfo.code}</dd>
                </>
              )}
              {clientInfo.contact_email && (
                <>
                  <dt className="text-muted-foreground">Contact Email</dt>
                  <dd>{clientInfo.contact_email}</dd>
                </>
              )}
              {clientInfo.phone && (
                <>
                  <dt className="text-muted-foreground">Phone</dt>
                  <dd>{clientInfo.phone}</dd>
                </>
              )}
            </dl>
          </CardContent>
        </Card>
      )}

      {/* Winery Info */}
      {orgInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Winery</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {orgInfo.logo_url && (
                <img
                  src={orgInfo.logo_url}
                  alt={orgInfo.name}
                  className="h-12 w-12 rounded-lg object-contain border"
                />
              )}
              <div>
                <p className="font-medium">{orgInfo.name}</p>
                <p className="text-sm text-muted-foreground">Your wines are managed here.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3 text-sm">
            <dt className="text-muted-foreground">Email</dt>
            <dd>{user?.email}</dd>
            <dt className="text-muted-foreground">Member since</dt>
            <dd>
              {profile?.created_at
                ? new Date(profile.created_at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : "—"}
            </dd>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
